import { apiClient } from '@/hooks/useAxios';
import { CommonRes } from '@/types';
import {
  AdminAnswerOptionSet,
  AdminQuestion,
  AdminQuestionModule,
  AdminUser,
  ApiLog,
  ApiLogListResponse,
  CreateAnswerOptionSetAsAdminRequest,
  CreateQuestionAsAdminRequest,
  CreateQuestionModuleAsAdminRequest,
  CreateQuestionnaireAsAdminRequest,
  CreateUserAsAdminRequest,
  LogType,
  UpdateAnswerOptionSetAsAdminRequest,
  UpdateQuestionAsAdminRequest,
  UpdateQuestionModuleAsAdminRequest,
  UpdateQuestionnaireAsAdminRequest,
  UpdateUserAsAdminRequest,
} from '@/types/apis/admin';
import { AdminQuestionnaire } from '@/types/components';

const API_PATH = '/admin';

// ===== USER MANAGEMENT =====

export const fetchUsersAsAdmin = async (
  page: number,
  pageSize: number,
): Promise<
  CommonRes<{
    items: AdminUser[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>
> => {
  const res = await apiClient.get<
    CommonRes<{
      items: AdminUser[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/user?page=${page}&limit=${pageSize}`);
  return res.data;
};

export const fetchUserAsAdmin = async (
  userId: string,
): Promise<CommonRes<AdminUser>> => {
  const res = await apiClient.get<CommonRes<AdminUser>>(
    `${API_PATH}/user/${userId}`,
  );
  return res.data;
};

export const updateUserAsAdmin = async (
  userId: string,
  data: UpdateUserAsAdminRequest,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/user/${userId}`,
    data,
  );
  return res.data;
};

export const deleteUserAsAdmin = async (
  userId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.delete<CommonRes<{ message: string }>>(
    `${API_PATH}/user/${userId}`,
  );
  return res.data;
};

export const createUserAsAdmin = async (
  data: CreateUserAsAdminRequest,
): Promise<CommonRes<{ id: string; message: string }>> => {
  const res = await apiClient.post<CommonRes<{ id: string; message: string }>>(
    `${API_PATH}/user`,
    data,
  );
  return res.data;
};

export const resendVerificationEmailAsAdmin = async (
  userId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.post<CommonRes<{ message: string }>>(
    `${API_PATH}/user/${userId}/resend-verification`,
  );
  return res.data;
};

export const resendPasswordResetEmailAsAdmin = async (
  userId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.post<CommonRes<{ message: string }>>(
    `${API_PATH}/user/${userId}/resend-password-reset`,
  );
  return res.data;
};

// ===== QUESTIONNAIRE MANAGEMENT =====

export const fetchQuestionnairesAsAdmin = async (
  page: number,
  pageSize: number,
  hasResult?: boolean,
  isResultComplete?: boolean,
): Promise<
  CommonRes<{
    items: AdminQuestionnaire[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>
> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('limit', String(pageSize));
  if (hasResult !== undefined) {
    params.append('hasResult', String(hasResult));
  }
  if (isResultComplete !== undefined) {
    params.append('isResultComplete', String(isResultComplete));
  }

  const res = await apiClient.get<
    CommonRes<{
      items: AdminQuestionnaire[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/questionnaire?${params.toString()}`);
  return res.data;
};

export const fetchQuestionnaireAsAdmin = async (
  questionnaireId: string,
): Promise<CommonRes<AdminQuestionnaire>> => {
  const res = await apiClient.get<CommonRes<AdminQuestionnaire>>(
    `${API_PATH}/questionnaire/${questionnaireId}`,
  );
  return res.data;
};

export const updateQuestionnaireAsAdmin = async (
  questionnaireId: string,
  data: UpdateQuestionnaireAsAdminRequest,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/questionnaire/${questionnaireId}`,
    data,
  );
  return res.data;
};

export const deleteQuestionnaireAsAdmin = async (
  questionnaireId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.delete<CommonRes<{ message: string }>>(
    `${API_PATH}/questionnaire/${questionnaireId}`,
  );
  return res.data;
};

export const createQuestionnaireAsAdmin = async (
  data: CreateQuestionnaireAsAdminRequest,
): Promise<CommonRes<{ id: string; message: string }>> => {
  const res = await apiClient.post<CommonRes<{ id: string; message: string }>>(
    `${API_PATH}/questionnaire`,
    data,
  );
  return res.data;
};

// ===== QUESTION MANAGEMENT =====

export const fetchQuestionsAsAdmin = async (
  page: number,
  pageSize: number,
  moduleId?: string,
  industryName?: string,
): Promise<
  CommonRes<{
    items: AdminQuestion[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>
> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
  });

  if (moduleId) {
    params.append('moduleId', moduleId);
  }

  if (industryName) {
    params.append('industryName', industryName);
  }

  const res = await apiClient.get<
    CommonRes<{
      items: AdminQuestion[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/question?${params.toString()}`);
  return res.data;
};

export const fetchQuestionAsAdmin = async (
  questionId: string,
): Promise<CommonRes<AdminQuestion>> => {
  const res = await apiClient.get<CommonRes<AdminQuestion>>(
    `${API_PATH}/question/${questionId}`,
  );
  return res.data;
};

export const updateQuestionAsAdmin = async (
  questionId: string,
  data: UpdateQuestionAsAdminRequest,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/question/${questionId}`,
    data,
  );
  return res.data;
};

export const deleteQuestionAsAdmin = async (
  questionId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.delete<CommonRes<{ message: string }>>(
    `${API_PATH}/question/${questionId}`,
  );
  return res.data;
};

export const createQuestionAsAdmin = async (
  data: CreateQuestionAsAdminRequest,
): Promise<CommonRes<{ id: string; message: string }>> => {
  const res = await apiClient.post<CommonRes<{ id: string; message: string }>>(
    `${API_PATH}/question`,
    data,
  );
  return res.data;
};

// ===== QUESTION MODULE MANAGEMENT =====

export const fetchQuestionModulesAsAdmin = async (
  page: number,
  pageSize: number,
): Promise<
  CommonRes<{
    items: AdminQuestionModule[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>
> => {
  const res = await apiClient.get<
    CommonRes<{
      items: AdminQuestionModule[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/questionModule?page=${page}&limit=${pageSize}`);
  return res.data;
};

export const fetchAllQuestionModulesAsAdmin = async (): Promise<
  CommonRes<{ items: AdminQuestionModule[] }>
> => {
  const res = await apiClient.get<
    CommonRes<{
      items: AdminQuestionModule[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/questionModule?page=1&limit=1000`);
  return { ...res.data, data: { items: res.data.data.items } };
};

export const fetchQuestionModuleAsAdmin = async (
  moduleId: string,
): Promise<CommonRes<AdminQuestionModule>> => {
  const res = await apiClient.get<CommonRes<AdminQuestionModule>>(
    `${API_PATH}/questionModule/${moduleId}`,
  );
  return res.data;
};

export const updateQuestionModuleAsAdmin = async (
  moduleId: string,
  data: UpdateQuestionModuleAsAdminRequest,
): Promise<CommonRes<AdminQuestionModule>> => {
  const res = await apiClient.put<CommonRes<AdminQuestionModule>>(
    `${API_PATH}/questionModule/${moduleId}`,
    data,
  );
  return res.data;
};

export const deleteQuestionModuleAsAdmin = async (
  moduleId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.delete<CommonRes<{ message: string }>>(
    `${API_PATH}/questionModule/${moduleId}`,
  );
  return res.data;
};

export const createQuestionModuleAsAdmin = async (
  data: CreateQuestionModuleAsAdminRequest,
): Promise<CommonRes<AdminQuestionModule>> => {
  const res = await apiClient.post<CommonRes<AdminQuestionModule>>(
    `${API_PATH}/questionModule`,
    data,
  );
  return res.data;
};

export const batchUpdateQuestionsAsAdmin = async (
  questions: { id: string; order: number }[],
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/questions`,
    { questions },
  );
  return res.data;
};

export const batchUpdateQuestionModulesAsAdmin = async (
  modules: { id: string; order: number }[],
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/questionModules`,
    { modules },
  );
  return res.data;
};

// ===== ANSWER OPTION SET MANAGEMENT =====

export const fetchAnswerOptionSetsAsAdmin = async (
  page: number,
  pageSize: number,
): Promise<
  CommonRes<{
    items: AdminAnswerOptionSet[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>
> => {
  const res = await apiClient.get<
    CommonRes<{
      items: AdminAnswerOptionSet[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  >(`${API_PATH}/answerOptionSet?page=${page}&limit=${pageSize}`);
  return res.data;
};

export const fetchAnswerOptionSetAsAdmin = async (
  answerOptionSetId: string,
): Promise<CommonRes<AdminAnswerOptionSet>> => {
  const res = await apiClient.get<CommonRes<AdminAnswerOptionSet>>(
    `${API_PATH}/answerOptionSet/${answerOptionSetId}`,
  );
  return res.data;
};

export const updateAnswerOptionSetAsAdmin = async (
  answerOptionSetId: string,
  data: UpdateAnswerOptionSetAsAdminRequest,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.put<CommonRes<{ message: string }>>(
    `${API_PATH}/answerOptionSet/${answerOptionSetId}`,
    data,
  );
  return res.data;
};

export const deleteAnswerOptionSetAsAdmin = async (
  answerOptionSetId: string,
): Promise<CommonRes<{ message: string }>> => {
  const res = await apiClient.delete<CommonRes<{ message: string }>>(
    `${API_PATH}/answerOptionSet/${answerOptionSetId}`,
  );
  return res.data;
};

export const createAnswerOptionSetAsAdmin = async (
  data: CreateAnswerOptionSetAsAdminRequest,
): Promise<CommonRes<{ id: string; message: string }>> => {
  const res = await apiClient.post<CommonRes<{ id: string; message: string }>>(
    `${API_PATH}/answerOptionSet`,
    data,
  );
  return res.data;
};

export const fetchApiLogsAsAdmin = async (params: {
  page?: number;
  limit?: number;
  type?: LogType;
  userId?: string;
  requestId?: string;
  logId?: string;
  statusCode?: number;
  isError?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<CommonRes<ApiLogListResponse>> => {
  const res = await apiClient.get<CommonRes<ApiLogListResponse>>(
    `${API_PATH}/api-log`,
    { params },
  );
  return res.data;
};

export const fetchApiLogAsAdmin = async (
  id: string,
): Promise<CommonRes<ApiLog>> => {
  const res = await apiClient.get<CommonRes<ApiLog>>(
    `${API_PATH}/api-log/${id}`,
  );
  return res.data;
};

export const deleteApiLogAsAdmin = async (
  id: string,
): Promise<CommonRes<null>> => {
  const res = await apiClient.delete<CommonRes<null>>(
    `${API_PATH}/api-log/${id}`,
  );
  return res.data;
};

export const cleanupApiLogsAsAdmin = async (
  olderThanDays?: number,
): Promise<CommonRes<{ deletedCount: number }>> => {
  const res = await apiClient.post<CommonRes<{ deletedCount: number }>>(
    `${API_PATH}/api-log/cleanup`,
    {
      olderThanDays: olderThanDays ?? 30,
    },
  );
  return res.data;
};

// ===== RESULT MANAGEMENT =====

export const fetchResultsAsAdmin = async (
  page: number,
  pageSize: number,
): Promise<CommonRes<import('@/types/apis/admin').AdminResultsResponse>> => {
  const res = await apiClient.get<
    CommonRes<import('@/types/apis/admin').AdminResultsResponse>
  >(`${API_PATH}/result?page=${page}&limit=${pageSize}`);
  return res.data;
};

export const fetchResultStatistics = async (): Promise<
  CommonRes<import('@/types/apis/admin').ResultStatistics>
> => {
  const res = await apiClient.get<
    CommonRes<import('@/types/apis/admin').ResultStatistics>
  >(`${API_PATH}/result/statistics`);
  return res.data;
};
